import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Chip,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Button,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  EventAvailable as AvailabilityIcon,
  HourglassEmpty as BenchIcon,
  Warning as OverloadedIcon,
  CheckCircle as OptimalIcon,
  Refresh as RefreshIcon,
  PersonSearch as SearchIcon
} from '@mui/icons-material';
import axios from 'axios';

const ResourceCapacity = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({
    totalEmployees: 0,
    benchCount: 0,
    underutilizedCount: 0,
    healthyCount: 0,
    highUtilizationCount: 0,
    overloadedCount: 0,
    totalAvailableHours: 0
  });

  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [benchOnly, setBenchOnly] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5003';

  const fetchCapacityData = async () => {
    try {
      setLoading(true);
      let token = localStorage.getItem('token') || '';
      if (token.startsWith('"') && token.endsWith('"')) {
        token = token.slice(1, -1);
      }

      const response = await axios.get(`${API_URL}/api/resource-planning/capacity`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          month: selectedMonth,
          year: selectedYear,
          department: selectedDepartment || undefined,
          benchOnly: benchOnly || undefined
        }
      });

      if (response.data.success) {
        setData(response.data.data || []);
        setSummary(response.data.summary || {});
      }
    } catch (err) {
      //console.error('Error fetching capacity data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCapacityData();
  }, [selectedMonth, selectedYear, selectedDepartment, benchOnly]);

  const getStatusChip = (status) => {
    switch (status) {
      case 'Bench':
        return <Chip label="Bench (Available)" color="success" size="small" sx={{ fontWeight: 'bold' }} />;
      case 'Overloaded':
        return <Chip label="Overloaded" color="error" size="small" sx={{ fontWeight: 'bold' }} />;
      case 'Under-utilized':
        return <Chip label="Under-utilized" color="warning" size="small" sx={{ fontWeight: 'bold' }} />;
      case 'High Utilization':
        return <Chip label="High Utilization" color="info" size="small" sx={{ fontWeight: 'bold' }} />;
      default:
        return <Chip label="Optimal" color="primary" size="small" sx={{ fontWeight: 'bold' }} />;
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
             Resource Availability
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Monitor net working capacity, free hours, and identify available bench employees for project allocation.
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchCapacityData}
          size="small"
        >
          Refresh Capacity
        </Button>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ borderLeft: '4px solid #2e7d32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary">Total Bench Staff</Typography>
                <BenchIcon sx={{ color: '#2e7d32' }} />
              </Box>
              <Typography variant="h5" fontWeight="bold" sx={{ mt: 1, color: '#2e7d32' }}>
                {summary.benchCount || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Ready for new project assignments
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ borderLeft: '4px solid #1976d2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary">Total Available Hours</Typography>
                <AvailabilityIcon color="primary" />
              </Box>
              <Typography variant="h5" fontWeight="bold" sx={{ mt: 1, color: '#1976d2' }}>
                {summary.totalAvailableHours || 0} hrs
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Net unallocated workforce capacity
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ borderLeft: '4px solid #d32f2f' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary">Overloaded Staff</Typography>
                <OverloadedIcon sx={{ color: '#d32f2f' }} />
              </Box>
              <Typography variant="h5" fontWeight="bold" sx={{ mt: 1, color: '#d32f2f' }}>
                {summary.overloadedCount || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Allocated &gt; 100% capacity
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2} sx={{ borderLeft: '4px solid #ed6c02' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary">Total Technical Workforce</Typography>
                <OptimalIcon sx={{ color: '#ed6c02' }} />
              </Box>
              <Typography variant="h5" fontWeight="bold" sx={{ mt: 1 }}>
                {summary.totalEmployees || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Active project & engineering staff
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filter Controls */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Month</InputLabel>
              <Select
                value={selectedMonth}
                label="Month"
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((m, idx) => (
                  <MenuItem key={idx + 1} value={idx + 1}>{m}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                label="Year"
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {[2024, 2025, 2026, 2027].map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Department</InputLabel>
              <Select
                value={selectedDepartment}
                label="Department"
                onChange={(e) => setSelectedDepartment(e.target.value)}
              >
                <MenuItem value="">All Departments</MenuItem>
                <MenuItem value="Engineering">Engineering</MenuItem>
                <MenuItem value="Eng">Eng</MenuItem>
                <MenuItem value="IT">IT</MenuItem>
                <MenuItem value="Product">Product</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={benchOnly}
                onChange={(e) => setBenchOnly(e.target.checked)}
                color="success"
              />
            }
            label={<Typography variant="body2" fontWeight="bold">🟢 Show Bench Staff Only</Typography>}
          />
        </Box>
      </Paper>

      {/* Main Capacity & Bench Table */}
      <Paper elevation={3}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell><strong>Employee Name & Role</strong></TableCell>
                  <TableCell><strong>Department</strong></TableCell>
                  <TableCell align="center"><strong>Net Working Capacity</strong></TableCell>
                  <TableCell align="center"><strong>Allocated Hours</strong></TableCell>
                  <TableCell align="center"><strong>Free Working Hours</strong></TableCell>
                  <TableCell style={{ width: '25%' }}><strong>Availability %</strong></TableCell>
                  <TableCell align="center"><strong>Status</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No employees found matching the capacity criteria.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((row) => (
                    <TableRow key={row.employee_id} hover sx={{ bgcolor: row.status === 'Bench' ? '#f0fdf4' : 'inherit' }}>
                      <TableCell>
                        <Typography variant="body1" fontWeight="bold">
                          {row.employeeName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.designation} (ID: {row.employee_id})
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip label={row.department} size="small" variant="outlined" />
                      </TableCell>

                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="medium">
                          {row.netEffectiveCapacityHours} hrs
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          ({row.grossMonthlyHours}h baseline - {row.leaveHours + row.holidayHours}h off)
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography variant="body2" fontWeight="bold" color={row.allocatedPercentage > 100 ? 'error' : 'text.primary'}>
                          {row.allocatedHours} hrs ({row.allocatedPercentage}%)
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography variant="body1" fontWeight="bold" color={row.freeAvailableHours > 0 ? 'success.main' : 'text.secondary'}>
                          {row.freeAvailableHours} hrs
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(row.freeAvailablePercentage, 100)}
                              color={row.freeAvailablePercentage >= 50 ? 'success' : row.freeAvailablePercentage > 0 ? 'warning' : 'inherit'}
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                          </Box>
                          <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 40 }}>
                            {row.freeAvailablePercentage}%
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                        {getStatusChip(row.status)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default ResourceCapacity;
